// Global Instructions Rule Applied!
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Empty, Spin } from 'antd'

const CSS_PX_PER_INCH = 96
// Portrait-native paper widths in inches; landscape swaps in the height.
const PAGE_INCHES: Record<string, { width: number; height: number }> = {
  letter: { width: 8.5, height: 11 },
  a4: { width: 8.27, height: 11.69 },
  legal: { width: 8.5, height: 14 },
  slide: { width: 7.5, height: 13.333 }
}
// Breathing room either side of the sheet so it does not touch the frame.
const HORIZONTAL_PADDING_PX = 32

export interface ReportPreviewPaneProps {
  blobUrl: string | null
  pageCount: number | null
  loading: boolean
  error: string | null
  emptyText?: string
  /** Paper geometry of the rendered report; defaults preserve letter-portrait. */
  pageSize?: string
  orientation?: 'portrait' | 'landscape'
  /**
   * Called when the document reports a clicked in-app entity link
   * (postMessage from the sandboxed frame). The pane verifies the message
   * came from ITS iframe; the caller validates module/id before acting.
   */
  onAppLink?: (payload: { module: string; id: string; label: string }) => void
}

function pageWidthPx(pageSize: string | undefined, orientation: string | undefined): number {
  const paper = PAGE_INCHES[pageSize ?? 'letter'] ?? PAGE_INCHES.letter
  const width = orientation === 'landscape' ? paper.height : paper.width
  return width * CSS_PX_PER_INCH
}

/**
 * Fixed-paper preview of a rendered document report.
 *
 * The document arrives as a blob URL (see documentReportsService) because an
 * iframe src cannot carry an Authorization header. The sheet is a fixed
 * physical width (from the report's cfg geometry) scaled down to fit the
 * available width, rather than letting the iframe reflow: the whole point of
 * the document is that it is laid out at a fixed physical size, and a
 * reflowed preview would stop matching the PDF.
 *
 * Sandbox is `allow-scripts` WITHOUT `allow-same-origin`: the frame gets an
 * opaque origin, so the only thing a script in the document can do to the app
 * is postMessage — which is exactly the channel the in-app entity links use.
 * (Never add allow-same-origin alongside allow-scripts here: a blob URL
 * inherits the app's origin, and that pair would let document scripts reach
 * straight into the parent.)
 */
export function ReportPreviewPane({
  blobUrl,
  pageCount,
  loading,
  error,
  emptyText = 'Choose a report and press Apply to preview it.',
  pageSize = 'letter',
  orientation = 'portrait',
  onAppLink
}: ReportPreviewPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [scale, setScale] = useState(1)

  const widthPx = pageWidthPx(pageSize, orientation)

  const recomputeScale = useCallback(() => {
    const width = containerRef.current?.clientWidth
    if (!width) return
    const available = width - HORIZONTAL_PADDING_PX
    // Never scale up — a page larger than life reads as a rendering bug.
    setScale(Math.min(1, available / widthPx))
  }, [widthPx])

  useEffect(() => {
    recomputeScale()
    const element = containerRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(recomputeScale)
    observer.observe(element)
    return () => observer.disconnect()
  }, [recomputeScale])

  useEffect(() => {
    if (!onAppLink) return
    const handleMessage = (event: MessageEvent) => {
      // Source identity is the gate: the frame has an opaque origin (its
      // messages arrive with origin "null"), so an origin check cannot
      // distinguish it — but only OUR iframe's contentWindow matches here.
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return
      const data = event.data as { type?: string; module?: string; id?: string; label?: string }
      if (data?.type !== 'document-report-link') return
      if (typeof data.module !== 'string' || typeof data.id !== 'string') return
      onAppLink({ module: data.module, id: data.id, label: String(data.label ?? '') })
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onAppLink])

  return (
    <div ref={containerRef} className='relative h-full min-h-0 w-full overflow-auto bg-gray-100'>
      {loading && (
        <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/60'>
          <Spin tip='Rendering report…' />
        </div>
      )}

      {!loading && error && (
        <div className='flex h-full items-center justify-center p-6'>
          <Empty description={error} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}

      {!loading && !error && !blobUrl && (
        <div className='flex h-full items-center justify-center p-6'>
          <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}

      {blobUrl && !error && (
        <div
          style={{
            width: widthPx * scale,
            // transform does not affect layout, so the wrapper reserves the
            // scaled footprint itself or the scroll area would be wrong.
            height: '100%',
            margin: '0 auto'
          }}
        >
          <div
            style={{
              width: widthPx,
              height: `${100 / scale}%`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left'
            }}
          >
            <iframe
              ref={iframeRef}
              key={blobUrl}
              src={blobUrl}
              title={`Report preview${pageCount ? ` — ${pageCount} pages` : ''}`}
              className='h-full w-full border-0'
              sandbox='allow-scripts'
            />
          </div>
        </div>
      )}
    </div>
  )
}
